import styled from 'styled-components';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useMemo, useState } from 'react';
import Pagination from '../../../components/table/pagination';
import { IndeterminateCheckbox } from '../../../components/table/checkbox';
import Select from 'react-select';
import { ConfirmModal } from '../../../components/modal/confirm.modal';
import { AlertModal } from '../../../components/modal/alert.modal';
import { useHistory } from 'react-router-dom';
import { fetchAdminDeleteInquiry } from '../../../api/admin.inquiry';

const tableSort = [
  { value: '10', label: '10개씩 보기' },
  { value: '20', label: '20개씩 보기' },
  { value: '50', label: '50개씩 보기' },
];

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface InquiryDtoRx {
  inquiryId: number;
  title: string;
  createdAt: string;
  passid: string;
  state: string;
}
// AdmininquiryTableComp 컴포넌트
interface AdminInquiryTableCompProps {
  data: InquiryDtoRx[];
  setInquiryList: React.Dispatch<React.SetStateAction<InquiryDtoRx[] | null>>; // 데이터 갱신 함수 (삭제 후 업데이트)
}

export const AdminInquiryTableComp = ({
  data,
  setInquiryList,
}: AdminInquiryTableCompProps) => {
  const { register, handleSubmit } = useForm();

  const history = useHistory();

  const onSubmit = (data: any) => {};

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // 모달
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const handleConfirmClose = () => {
    setConfirmModal(false);
  };

  const columns = useMemo<ColumnDef<InquiryDtoRx>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          // 전체 선택이 필요한 경우
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomePageRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
                onClick: (e) => e.stopPropagation(),
              }}
            />
          </div>
        ),
        size: 45,
      },
      {
        header: '번호',
        id: 'number', // 넘버링을 위한 id
        cell: ({ row }) => row.index + 1, // 각 행의 인덱스에 1을 더해 넘버링
        size: 76,
      },
      {
        header: '문의 제목',
        accessorKey: 'title',
        cell: (info) => info.getValue(),
        size: 160,
      },
      {
        header: '등록일',
        accessorKey: 'createdAt',
        cell: (info) => info.getValue(),
        size: 120,
      },
      {
        header: '등록아이디',
        accessorKey: 'passid',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 120,
      },
      {
        header: '답변 여부',
        accessorKey: 'state',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 160,
      },
    ],
    [register],
  );
  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      pagination,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  const selectedInquiryIds = useMemo(() => {
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key]) // 선택된 행만 필터링
      .map((key) => {
        const row = table.getRowModel().rows[parseInt(key)];
        return row.original.inquiryId; // 선택된 행의 original 데이터에서 inquiryId 가져오기
      });
  }, [rowSelection, data]);

  console.log(selectedInquiryIds);

  const handleDelete = async () => {
    if (selectedInquiryIds.length === 0) {
      setModalMessage('삭제할 문의를 선택하세요.');
      setIsModalOpen(true);
      return;
    }

    console.log(selectedInquiryIds);

    //삭제 API 호출
    try {
      const response = await fetchAdminDeleteInquiry({
        inquiryId: selectedInquiryIds,
      });
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
      } else {
        setModalMessage(response.message);
        setIsModalOpen(true);
      }

      // 삭제된 항목을 로컬 상태에서 제거
      const updatedData = data.filter(
        (inquiry) => !selectedInquiryIds.includes(inquiry.inquiryId),
      );
      setInquiryList(updatedData); // 상위 컴포넌트의 상태 업데이트
      setRowSelection({}); // 선택된 행 초기화
    } catch (error) {
      console.error('문의 삭제 실패:', error);
      setModalMessage('문의 삭제에 실패했습니다.');
      setIsModalOpen(true);
    }
  };

  const handleConfirmAction = () => {
    setConfirmModal(false);
    handleDelete();
  };

  const handlePageSizeChange = (selectedOption: any) => {
    const newSize = selectedOption ? parseInt(selectedOption.value, 10) : 10; // 선택되지 않은 경우 기본값으로 10을 사용
    table.setPageSize(newSize);
  };
  const selectedValue = tableSort.find(
    (option) => option.value === String(table.getState().pagination.pageSize),
  );

  const handleRowClick = (rowData: InquiryDtoRx) => {
    console.log('Clicked row data:', rowData);
    // 여기에 클릭 시 실행할 동작을 정의하세요.
    // 예: rowData에 대한 상세 정보 보기, 수정하기, 모달 열기 등
    history.push({
      pathname: `/admin/inquiry/list/detail/${rowData.inquiryId}`,
    });
  };

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="table-head-wrap">
          <div className="left">
            <span className="table-total-row">
              전체 {table.getRowCount()}건
            </span>
          </div>
          <div className="right">
            <Select
              className="search-select"
              classNamePrefix="react-select"
              options={tableSort}
              onChange={handlePageSizeChange}
              components={{
                IndicatorSeparator: () => null,
              }}
              value={
                tableSort.find(
                  (option) =>
                    option.value ===
                    String(table.getState().pagination.pageSize),
                ) || tableSort[0]
              }
            />
            <div className="btn-box">
              <button
                className="delete"
                onClick={() => {
                  setConfirmModal(true);
                }}
              >
                선택 삭제
              </button>
            </div>
          </div>
        </div>
        <table>
          <thead>
            {table.getHeaderGroups().map((item) => (
              <tr key={item.id}>
                {item.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row.original)} // 행을 클릭할 때 handleRowClick 호출
                style={{ cursor: 'pointer' }} // 마우스 커서를 클릭 모양으로 변경
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    //className={cell.column.columnDef.meta?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={pagination.pageIndex}
          pages={table.getPageCount()}
          onPageChange={(page) => {
            table.setPageIndex(page);
          }}
        />
      </form>

      {confirmModal && (
        <ConfirmModal
          handleConfirmClose={handleConfirmClose}
          confirmText={'선택한 회원을 삭제하시겠습니까?'}
          onClick={handleConfirmAction} // 확인 시 handleConfirmAction 호출
        />
      )}
      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

// style
const Container = styled.section`
  max-width: 140rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 11.4rem;
  margin: 0 auto;
  form {
    table {
      margin-top: 2.4rem;
      width: 100%;
      font-size: 1.3rem;
      line-height: 1;
      letter-spacing: -5%;

      input[type='checkbox'] {
        width: 2rem;
        height: 2rem;
        border-color: ${(props) => props.theme.btnBlueColor};
      }

      thead {
        border-top: 2px solid ${(props) => props.theme.btnBlueColor};
        th {
          background-color: ${(props) => props.theme.textWhiteColor};
        }
      }
      th,
      td {
        padding: 1rem 1rem;
        border-bottom: 1px solid #f0f0f0;
        text-align: center;
        white-space: nowrap;
        .datail-btn {
          text-decoration: underline;
          cursor: pointer;
        }
        vertical-align: middle;
      }
      tbody {
        td {
          padding: 0.8rem 0.23rem;
          cursor: pointer;
        }
      }
      td.sm-padding {
        padding: 0.9rem 0;
      }
      .left-align {
        text-align: left;
      }
      .px-1 {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .table-input {
        input {
          border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
          font-size: 1.3rem;
          height: 2.8rem;
          border-radius: 0.8rem;
          max-width: 6rem;
        }
      }
      tfoot {
        background-color: ${(props) => props.theme.costBgColor};
        font-weight: 600;
        tr {
          td:first-child {
            text-align: left;
            padding-left: 2rem;
          }
        }
      }
      .state {
        width: 4.2rem;
        height: 2.4rem;
        font-size: 1.2rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.4rem;
        margin: 0 auto;
        &.assigned {
          color: #727272;
          background-color: #e0e0e0;
        }
        &.activation {
          color: ${(props) => props.theme.textWhiteColor};
          background-color: #62cc9f;
        }
      }
    }
    .table-head-wrap {
      margin-top: 3.2rem;
      margin-bottom: 1.2rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .left {
        .table-total-row {
          padding: 0 1px;
          font-size: 1.2rem;
          font-weight: 600;
          color: ${(props) => props.theme.gray500};
        }
      }
      .right {
        display: flex;
        gap: 1.2rem;
        align-items: center;
        .search-select {
          width: 18rem;
          font-size: 1.3rem;
          .react-select__placeholder {
            color: ${(props) => props.theme.searchPlaceholderColor};
          }
          .react-select__control {
            border-color: ${(props) => props.theme.searchBoxBorderedColor};
            border-radius: 0.8rem;
            .react-select__value-container {
              padding: 0.8rem 1.6rem;
            }
          }
        }
        .btn-box {
          display: flex;
          gap: 0.8rem;
          button {
            width: 8rem;
            font-size: 1.4rem;
            font-weight: 500;
            padding: 1.1rem 0;
            border-radius: 0.4rem;
            outline: none;
            border: 1px solid;
            background-color: ${(props) => props.theme.textWhiteColor};
            &.delete {
              color: #81858e;
              border-color: #dbdee3;
            }
            &.approval {
              color: ${(props) => props.theme.btnBlueColor};
              border-color: ${(props) => props.theme.btnBlueColor};
            }
          }
        }
      }
    }
  }
`;
